import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useQuestionResponsesByUser,
  useCreateQuestionResponse,
  useUpdateQuestionResponse,
} from "@/hooks/api/useQuestionResponses";
import {
  QUESTION_ID_WEIGHT,
  QUESTION_ID_HEIGHT,
  QUESTION_ID_WAIST,
  QUESTION_ID_BLOOD_PRESSURE,
} from "@/lib/well-known-questions";
import { getCurrentAuthUserId } from "@/lib/current-user";

const WEIGHT_QUESTION_TEXT = "Vekt";
const HEIGHT_QUESTION_TEXT = "Høyde";
const WAIST_QUESTION_TEXT = "Midjemål";
const BP_QUESTION_TEXT = "Blodtrykk";
const BP_SECONDARY_LABEL = "Diastolisk (lav)";

function shortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")}.${d.getFullYear().toString().slice(2)}`;
}

function isToday(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return d.toDateString() === now.toDateString();
}

export function ConsultationVitals({ authUserId }: { authUserId: string }) {
  const { data: responses } = useQuestionResponsesByUser(authUserId);
  const createResponse = useCreateQuestionResponse();
  const updateResponse = useUpdateQuestionResponse();

  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [waist, setWaist] = useState("");
  const [systolic, setSystolic] = useState("");
  const [diastolic, setDiastolic] = useState("");

  // If a measurement was already saved today, edits update that same row
  // instead of creating a new one — within one consultation, "save" upserts.
  const [todayWeightId, setTodayWeightId] = useState<string | null>(null);
  const [todayHeightId, setTodayHeightId] = useState<string | null>(null);
  const [todayWaistId, setTodayWaistId] = useState<string | null>(null);
  const [todayBPId, setTodayBPId] = useState<string | null>(null);
  const [didPrefill, setDidPrefill] = useState(false);

  const sortedWeight = useMemo(() => {
    if (!responses) return [];
    return responses
      .filter((r) => r.questionId === QUESTION_ID_WEIGHT)
      .sort(
        (a, b) =>
          new Date(b.measuredAt).getTime() - new Date(a.measuredAt).getTime(),
      );
  }, [responses]);

  const sortedHeight = useMemo(() => {
    if (!responses) return [];
    return responses
      .filter((r) => r.questionId === QUESTION_ID_HEIGHT)
      .sort(
        (a, b) =>
          new Date(b.measuredAt).getTime() - new Date(a.measuredAt).getTime(),
      );
  }, [responses]);

  const sortedWaist = useMemo(() => {
    if (!responses) return [];
    return responses
      .filter((r) => r.questionId === QUESTION_ID_WAIST)
      .sort(
        (a, b) =>
          new Date(b.measuredAt).getTime() - new Date(a.measuredAt).getTime(),
      );
  }, [responses]);

  const sortedBP = useMemo(() => {
    if (!responses) return [];
    return responses
      .filter((r) => r.questionId === QUESTION_ID_BLOOD_PRESSURE)
      .sort(
        (a, b) =>
          new Date(b.measuredAt).getTime() - new Date(a.measuredAt).getTime(),
      );
  }, [responses]);

  const latestWeight = sortedWeight[0] ?? null;
  const latestHeight = sortedHeight[0] ?? null;
  const latestWaist = sortedWaist[0] ?? null;
  const latestBP = sortedBP[0] ?? null;

  // Prefill once after responses load with the most recent value for each
  // vital — regardless of who entered it or when. Patient self-entries and
  // other admins' entries fill the inputs so the doctor can confirm with
  // one keystroke instead of re-typing.
  //
  // Separately, if a row from today exists AND was entered by the current
  // admin, treat it as the active consultation row so further saves update
  // instead of insert. Saves against a prefilled row from someone else (or
  // a prior day) create a new row.
  useEffect(() => {
    if (didPrefill || !responses) return;
    const currentAdminId = getCurrentAuthUserId();
    if (latestWeight) setWeight(latestWeight.response);
    if (latestHeight) setHeight(latestHeight.response);
    if (latestWaist) setWaist(latestWaist.response);
    if (latestBP) {
      setSystolic(latestBP.response);
      setDiastolic(latestBP.secondaryQuestionResponse ?? "");
    }
    if (currentAdminId) {
      if (
        latestWeight &&
        isToday(latestWeight.measuredAt) &&
        latestWeight.enteredBy === currentAdminId
      ) {
        setTodayWeightId(latestWeight.id);
      }
      if (
        latestHeight &&
        isToday(latestHeight.measuredAt) &&
        latestHeight.enteredBy === currentAdminId
      ) {
        setTodayHeightId(latestHeight.id);
      }
      if (
        latestWaist &&
        isToday(latestWaist.measuredAt) &&
        latestWaist.enteredBy === currentAdminId
      ) {
        setTodayWaistId(latestWaist.id);
      }
      if (
        latestBP &&
        isToday(latestBP.measuredAt) &&
        latestBP.enteredBy === currentAdminId
      ) {
        setTodayBPId(latestBP.id);
      }
    }
    setDidPrefill(true);
  }, [
    didPrefill,
    responses,
    latestWeight,
    latestHeight,
    latestWaist,
    latestBP,
  ]);

  const hasInput =
    weight.trim() !== "" ||
    height.trim() !== "" ||
    waist.trim() !== "" ||
    (systolic.trim() !== "" && diastolic.trim() !== "");
  const onlyOneBpField = (systolic.trim() === "") !== (diastolic.trim() === "");
  const isPending = createResponse.isPending || updateResponse.isPending;

  const handleSave = async () => {
    if (!hasInput || onlyOneBpField) return;
    const enteredBy = getCurrentAuthUserId();
    if (!enteredBy) {
      toast.error("Klarte ikke å identifisere innlogget bruker");
      return;
    }
    const measuredAt = new Date().toISOString();
    type SavedKind = "weight" | "height" | "waist" | "bp";
    const tasks: Promise<{ kind: SavedKind; id: string } | null>[] = [];

    // The state-tracked todayXId is only set after a save within this mount,
    // so on a remount or page reload it is null even when a same-day, same-admin
    // row already exists. Fall back to deriving it from the current responses so
    // edits keep upserting instead of inserting duplicates.
    const ownTodayId = (
      sorted: { id: string; measuredAt: string; enteredBy: string | null }[],
    ): string | null => {
      const latest = sorted[0];
      if (!latest) return null;
      if (!isToday(latest.measuredAt)) return null;
      if (latest.enteredBy !== enteredBy) return null;
      return latest.id;
    };
    const targetWeightId = todayWeightId ?? ownTodayId(sortedWeight);
    const targetHeightId = todayHeightId ?? ownTodayId(sortedHeight);
    const targetWaistId = todayWaistId ?? ownTodayId(sortedWaist);
    const targetBPId = todayBPId ?? ownTodayId(sortedBP);

    if (weight.trim()) {
      if (targetWeightId) {
        tasks.push(
          updateResponse
            .mutateAsync({
              id: targetWeightId,
              authUserId,
              data: { response: weight.trim(), measuredAt, enteredBy },
            })
            .then((r) => ({ kind: "weight" as const, id: r.id })),
        );
      } else {
        tasks.push(
          createResponse
            .mutateAsync({
              authUserId,
              questionId: QUESTION_ID_WEIGHT,
              questionText: WEIGHT_QUESTION_TEXT,
              response: weight.trim(),
              measuredAt,
              enteredBy,
            })
            .then((r) => ({ kind: "weight" as const, id: r.id })),
        );
      }
    }

    if (height.trim()) {
      if (targetHeightId) {
        tasks.push(
          updateResponse
            .mutateAsync({
              id: targetHeightId,
              authUserId,
              data: { response: height.trim(), measuredAt, enteredBy },
            })
            .then((r) => ({ kind: "height" as const, id: r.id })),
        );
      } else {
        tasks.push(
          createResponse
            .mutateAsync({
              authUserId,
              questionId: QUESTION_ID_HEIGHT,
              questionText: HEIGHT_QUESTION_TEXT,
              response: height.trim(),
              measuredAt,
              enteredBy,
            })
            .then((r) => ({ kind: "height" as const, id: r.id })),
        );
      }
    }

    if (waist.trim()) {
      if (targetWaistId) {
        tasks.push(
          updateResponse
            .mutateAsync({
              id: targetWaistId,
              authUserId,
              data: { response: waist.trim(), measuredAt, enteredBy },
            })
            .then((r) => ({ kind: "waist" as const, id: r.id })),
        );
      } else {
        tasks.push(
          createResponse
            .mutateAsync({
              authUserId,
              questionId: QUESTION_ID_WAIST,
              questionText: WAIST_QUESTION_TEXT,
              response: waist.trim(),
              measuredAt,
              enteredBy,
            })
            .then((r) => ({ kind: "waist" as const, id: r.id })),
        );
      }
    }

    if (systolic.trim() && diastolic.trim()) {
      if (targetBPId) {
        tasks.push(
          updateResponse
            .mutateAsync({
              id: targetBPId,
              authUserId,
              data: {
                response: systolic.trim(),
                secondaryQuestion: BP_SECONDARY_LABEL,
                secondaryQuestionResponse: diastolic.trim(),
                measuredAt,
                enteredBy,
              },
            })
            .then((r) => ({ kind: "bp" as const, id: r.id })),
        );
      } else {
        tasks.push(
          createResponse
            .mutateAsync({
              authUserId,
              questionId: QUESTION_ID_BLOOD_PRESSURE,
              questionText: BP_QUESTION_TEXT,
              response: systolic.trim(),
              secondaryQuestion: BP_SECONDARY_LABEL,
              secondaryQuestionResponse: diastolic.trim(),
              measuredAt,
              enteredBy,
            })
            .then((r) => ({ kind: "bp" as const, id: r.id })),
        );
      }
    }

    try {
      const results = await Promise.all(tasks);
      for (const r of results) {
        if (!r) continue;
        if (r.kind === "weight") setTodayWeightId(r.id);
        if (r.kind === "height") setTodayHeightId(r.id);
        if (r.kind === "waist") setTodayWaistId(r.id);
        if (r.kind === "bp") setTodayBPId(r.id);
      }
      toast.success("Måling lagret");
    } catch {
      toast.error("Kunne ikke lagre måling");
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-admin-warn-300">
        <Activity className="h-3.5 w-3.5" />
        Målinger
      </div>

      <div className="space-y-2 rounded border border-admin-card-border/60 bg-admin-card/40 p-2.5">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-admin-text-subtle">
              Vekt (kg)
            </label>
            <Input
              type="number"
              inputMode="decimal"
              step="0.1"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="h-8 w-24 text-xs"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-admin-text-subtle">
              Høyde (cm)
            </label>
            <Input
              type="number"
              inputMode="decimal"
              step="0.1"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              className="h-8 w-24 text-xs"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-admin-text-subtle">
              Midjemål (cm)
            </label>
            <Input
              type="number"
              inputMode="decimal"
              step="0.1"
              value={waist}
              onChange={(e) => setWaist(e.target.value)}
              className="h-8 w-24 text-xs"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-admin-text-subtle">
              Blodtrykk (sys/dia)
            </label>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                inputMode="numeric"
                value={systolic}
                onChange={(e) => setSystolic(e.target.value)}
                className="h-8 w-20 text-xs"
                aria-label="Systolisk (høy)"
              />
              <span className="text-admin-text-subtle">/</span>
              <Input
                type="number"
                inputMode="numeric"
                value={diastolic}
                onChange={(e) => setDiastolic(e.target.value)}
                className="h-8 w-20 text-xs"
                aria-label="Diastolisk (lav)"
              />
              <span className="text-[10px] text-admin-text-subtle">mmHg</span>
            </div>
          </div>

          <Button
            size="sm"
            className="h-8 text-xs"
            disabled={!hasInput || onlyOneBpField || isPending}
            onClick={handleSave}
          >
            Lagre måling
          </Button>
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-admin-text-subtle">
          <span>
            Sist vekt:{" "}
            {latestWeight
              ? `${latestWeight.response} kg (${shortDate(latestWeight.measuredAt)})`
              : "ingen registrert"}
          </span>
          <span>
            Sist høyde:{" "}
            {latestHeight
              ? `${latestHeight.response} cm (${shortDate(latestHeight.measuredAt)})`
              : "ingen registrert"}
          </span>
          <span>
            Sist midje:{" "}
            {latestWaist
              ? `${latestWaist.response} cm (${shortDate(latestWaist.measuredAt)})`
              : "ingen registrert"}
          </span>
          <span>
            Sist BT:{" "}
            {latestBP
              ? `${latestBP.response}/${latestBP.secondaryQuestionResponse ?? "?"} (${shortDate(latestBP.measuredAt)})`
              : "ingen registrert"}
          </span>
        </div>

        {onlyOneBpField && (
          <p className="text-[11px] text-admin-negative">
            Fyll inn både systolisk og diastolisk for å lagre blodtrykk.
          </p>
        )}
      </div>
    </div>
  );
}
